import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, BASE_URL, colors } from "../shared/constants";

export function NoticesPage() {
  const [notices, setNotices] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to: "all", recipient: "", title: "", body: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [nRes, tRes] = await Promise.all([
          axios.get(`${API}/notices/`),
          axios.get(`${BASE_URL}/auth/technicians/`),
        ]);
        setNotices(nRes.data);
        setTechs(tRes.data);
      } catch (e) {
        console.error("Notices load error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openModal = () => {
    setForm({ to: "all", recipient: "", title: "", body: "" });
    setError("");
    setShowModal(true);
  };

  const handleSend = async () => {
    if (!form.body.trim()) { setError("Message is required."); return; }
    if (form.to === "user" && !form.recipient) { setError("Please select a recipient."); return; }
    setSending(true);
    setError("");
    try {
      const payload = {
        audience: "field_techs",
        title: form.title.trim() || "Notice",
        body: form.body.trim(),
        is_active: true,
        recipient: form.to === "user" ? parseInt(form.recipient) : null,
      };
      const res = await axios.post(`${API}/notices/`, payload);
      setNotices(prev => [res.data, ...prev]);
      setShowModal(false);
    } catch (e) {
      setError("Failed to send notice. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await axios.patch(`${API}/notices/${id}/`, { is_active: false });
      setNotices(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: "12px",
    borderLeft: `4px solid ${colors.amber}`,
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>Loading...</div>;

  return (
    <div style={{ padding: "28px", maxWidth: "780px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: colors.darkGrey, margin: 0 }}>Notices</h1>
          <p style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}>
            Send messages to field techs — they appear on their dashboard.
          </p>
        </div>
        <button
          onClick={openModal}
          style={{
            backgroundColor: "#16a34a",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          + Send Notice
        </button>
      </div>

      {/* Notice list */}
      {notices.length === 0 ? (
        <div style={{ ...cardStyle, borderLeft: "none", textAlign: "center", color: "#aaa", fontSize: "14px", padding: "40px" }}>
          No notices sent yet
        </div>
      ) : (
        notices.map(n => (
          <div key={n.id} style={cardStyle}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: "700", fontSize: "14px", color: colors.darkGrey }}>{n.title}</div>
                <div style={{ fontSize: "13px", color: colors.slate, marginTop: "5px", whiteSpace: "pre-wrap" }}>{n.body}</div>
                <div style={{ fontSize: "11px", color: "#aaa", marginTop: "8px" }}>
                  <span style={{ backgroundColor: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "10px", fontWeight: "600", fontSize: "11px" }}>
                    {n.recipient_name ? `→ ${n.recipient_name}` : "→ All Field Techs"}
                  </span>
                  <span style={{ marginLeft: "8px" }}>
                    Posted by {n.posted_by_name} · {new Date(n.posted_at).toLocaleDateString("en-AU")}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleDeactivate(n.id)}
                title="Remove notice"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", fontSize: "18px", lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
              >
                ✕
              </button>
            </div>
          </div>
        ))
      )}

      {/* Send Notice modal */}
      {showModal && (
        <>
          <div
            onClick={() => setShowModal(false)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 299 }}
          />
          <div style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: colors.white,
            borderRadius: "14px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            padding: "28px",
            zIndex: 300,
            width: "440px",
            maxWidth: "calc(100vw - 32px)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div style={{ fontSize: "17px", fontWeight: "700", color: colors.darkGrey }}>Send Notice</div>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate, lineHeight: 1 }}>✕</button>
            </div>

            {/* To field */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px", textTransform: "uppercase" }}>To</label>
              <select
                value={form.to}
                onChange={e => setForm(f => ({ ...f, to: e.target.value, recipient: "" }))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", backgroundColor: colors.white, color: colors.darkGrey }}
              >
                <option value="all">All Field Techs</option>
                <option value="user">Specific Person</option>
              </select>
            </div>

            {form.to === "user" && (
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Select Tech</label>
                <select
                  value={form.recipient}
                  onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", backgroundColor: colors.white, color: colors.darkGrey }}
                >
                  <option value="">— Select —</option>
                  {techs.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name || t.last_name ? `${t.first_name} ${t.last_name}`.trim() : t.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Title */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Title (optional)</label>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Schedule change"
                style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", boxSizing: "border-box" }}
              />
            </div>

            {/* Message */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Message</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your message here..."
                rows={4}
                style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
              />
            </div>

            {error && <div style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ padding: "9px 20px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "none", fontSize: "14px", cursor: "pointer", color: colors.slate }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                style={{ padding: "9px 24px", borderRadius: "8px", border: "none", backgroundColor: "#16a34a", color: "white", fontSize: "14px", fontWeight: "600", cursor: "pointer", opacity: sending ? 0.7 : 1 }}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
